// @ts-strict-ignore
import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Signal } from '@angular/core';
import { selectRouterParamData } from '@fc-store/selectors';
import { FeatureFlagService } from '@fc-core/feature-flags/feature-flag.service';
import { QuickCreationNav } from './quick-creation/quick-creation.nav';
import { quickCreationNavStructure } from './quick-creation/quick-creation-nav.structure';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { DialogsService, Employee } from '@fc-core';
import { DeveloperOptionsComponent } from '../../../dialogs/developer-options/developer-options.component';
import { DevCustomerSelectComponent } from '../../../dialogs/customer-chooser-dialog/dev-customer-select/dev-customer-select.component';
import { StatusSetService } from '@fc-shared/ui/status-set-dialog/status-set.service';
import { loadEmployee } from '@fc-store/actions';
import { isMobileSignal } from '@fc-shared/utils/is-mobile';

@Injectable({
  providedIn: 'root',
})
export class HeaderFacade {
  private store = inject(Store);
  private amplitude = inject(AmplitudeService);
  private router = inject(Router);
  private dialogs = inject(DialogsService);
  private statusSetService = inject(StatusSetService);

  private features = inject(FeatureFlagService).effectiveFeatures;

  private quickActionType = this.store.selectSignal(
    selectRouterParamData('quickActionType'),
  );

  showNews = signal(false);
  showEvents = signal(false);

  // ============================================
  // Quick Creation - Signals & Computations
  // ============================================

  private readonly isMobileViewport = isMobileSignal();

  quickCreationNavs = computed(() => {
    return quickCreationNavStructure.filter((nav) => {
      if (nav?.desktopOnly && this.isMobileViewport()) return false;
      if (nav?.featureKey) {
        return this.features()?.includes(nav.featureKey);
      }
      return true;
    });
  });

  quickCreation: Signal<QuickCreationNav> = computed(() => {
    return this.quickCreationNavs()
      .map((nav) => nav.children)
      .flat()
      .find((nav) => nav.type === this.quickActionType());
  });

  // ============================================
  // Quick Creation - Navigation
  // ============================================

  navigateQuickCreation(nav?: QuickCreationNav): void {
    const currentQuickCreation = nav ?? this.quickCreation();
    if (!currentQuickCreation) return;

    this.trackAndNavigate(currentQuickCreation);
  }

  private trackAndNavigate(nav: QuickCreationNav): void {
    const type = nav.type?.replace('-', '_') ?? '';
    this.amplitude.track(`create_dropdown_${type}`);

    if (nav.routerParams) {
      const { routerLink, queryParams } = nav.routerParams;
      this.navigateToRoute(routerLink, queryParams);
    } else {
      this.router.navigate(nav.path);
    }
  }

  private navigateToRoute(routerLink: string[], queryParams?: any): void {
    const currentPath = this.router.url.split('?')[0];
    const targetPath = routerLink.join('/');
    if (currentPath === targetPath) {
      this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    } else {
      this.router.navigate(routerLink, { queryParams });
    }
  }

  // ============================================
  // Employee Status
  // ============================================

  getStatusText(employee: Employee | undefined): string {
    return employee?.setstatus?.status
      ? `${employee.setstatus.icon} ${employee.setstatus.status}`
      : 'Update your status';
  }

  openStatusSetDialog(
    employeeId: string | undefined,
    currentStatus: any,
  ): void {
    if (!employeeId) return;

    this.amplitude.track('user_dropdown_click_status');

    this.statusSetService.openStatusSetDialog(
      'employee',
      employeeId,
      currentStatus,
      loadEmployee({ id: employeeId }),
    );
  }

  // ============================================
  // Dialog Management
  // ============================================

  openDeveloperOptions(): void {
    this.dialogs.openFullScreenDialog(DeveloperOptionsComponent);
  }

  openConfirmLogout(): void {
    this.amplitude.track('user_dropdown_click_logout');
    this.dialogs.openConfirmDialog({
      title: 'Logout?',
      message: 'Are you sure want to logout?',
      buttonText: 'Logout',
      action: () => this.router.navigate(['/logout']),
      buttonColor: 'accent',
    });
  }

  openChangeCustomer(): void {
    this.dialogs.openFullScreenDialog(DevCustomerSelectComponent);
  }
}
